import { useState } from "react";
import { Loader2, Save, Trash2, UserPlus, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ProspectForm } from "./ProspectForm";
import { ContactLog } from "./ContactLog";
import { ConvertDialog } from "./ConvertDialog";
import { EMPTY_PROSPECT, StatusPill } from "./crmShared";

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return Array.isArray(d) ? `${d[0]?.loc?.slice(-1)[0] || "Field"}: ${d[0]?.msg}` : d || "Couldn't save";
};

export const ProspectSheet = ({ prospect, onClose, onSaved, onDeleted }) => {
  const isNew = !prospect.id;
  const [v, setV] = useState({ ...EMPTY_PROSPECT, ...prospect });
  const [busy, setBusy] = useState("");
  const [convert, setConvert] = useState(false);

  const payload = () => Object.fromEntries(Object.keys(EMPTY_PROSPECT).map((k) => [k, v[k]]));
  const save = async () => {
    setBusy("save");
    try {
      const r = isNew ? await api.post("/crm/prospects", payload()) : await api.put(`/crm/prospects/${v.id}`, payload());
      setV(r.data); onSaved(r.data); toast.success(isNew ? "Prospect added" : "Prospect saved");
      if (isNew) onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${v.church_name} from the CRM?`)) return;
    setBusy("delete");
    try { await api.delete(`/crm/prospects/${v.id}`); onDeleted(v.id); toast.success("Prospect deleted"); onClose(); }
    catch { toast.error("Couldn't delete"); setBusy(""); }
  };
  const refreshed = (doc) => { setV((cur) => ({ ...cur, contacts: doc.contacts, last_contact_date: doc.last_contact_date, next_follow_up_date: doc.next_follow_up_date, status: doc.status, converted_user_id: doc.converted_user_id })); onSaved(doc); };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent data-testid="prospect-sheet" className="bg-[#0B0F19] border-gray-800 text-white w-full sm:max-w-[620px] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-white flex items-center gap-3">{isNew ? "New prospect" : v.church_name}{!isNew && <StatusPill status={v.status} id="prospect-sheet-status" />}</SheetTitle>
          <SheetDescription className="text-gray-400">{isNew ? "Add a church to your sales pipeline." : "Edit details, log contacts and move them through the pipeline."}</SheetDescription>
        </SheetHeader>

        {!isNew && (v.converted_user_id ? (
          <Link data-testid="prospect-customer-link" to={`/clients/${v.converted_user_id}`} className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 hover:bg-emerald-500/15"><CheckCircle2 className="h-4 w-4" /> Customer account created — open client profile <ExternalLink className="h-3.5 w-3.5 ml-auto" /></Link>
        ) : v.status === "Customer" && (
          <button data-testid="prospect-convert" onClick={() => setConvert(true)} className="mt-4 w-full flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 hover:bg-emerald-500/15"><UserPlus className="h-4 w-4" /> Create their customer account</button>
        ))}

        <div className="mt-6"><ProspectForm v={v} set={setV} /></div>
        <div className="mt-5 flex items-center gap-3">
          {!isNew && <button data-testid="prospect-delete" onClick={remove} disabled={!!busy} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-400"><Trash2 className="h-4 w-4" /> Delete</button>}
          <button data-testid="prospect-save" onClick={save} disabled={!!busy || !v.church_name.trim()} className="ml-auto inline-flex items-center gap-2 rounded-lg gold-gradient px-5 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Add prospect" : "Save changes"}
          </button>
        </div>

        {!isNew && (
          <div className="mt-8 pt-6 border-t border-gray-800">
            <h3 className="font-display font-semibold text-sm mb-3">Contact log</h3>
            <ContactLog prospect={v} onChange={refreshed} />
          </div>
        )}
        {convert && <ConvertDialog prospect={v} onClose={() => setConvert(false)} onDone={refreshed} />}
      </SheetContent>
    </Sheet>
  );
};
